import {
  AccordionContent,
  AccordionHeader,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
  Box,
  Button,
  Card,
  formatNumber,
  Inline,
  RadioButtonGroup,
  Skeleton,
  Stack,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  useCreateContractorInvoicePaymentMutation,
  useCreateContractorInvoiceQuoteMutation,
} from '@omnipresentgroup/domains';
import { ContractorInvoiceQuote } from '@omnipresentgroup/features';
import type {
  ContractorInvoice,
  ContractorPayment,
  PaymentMethod,
} from '@omnipresentgroup/types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { PaymentMethodSelector } from '../../../../../components';
import {
  CONTRACTOR_INVOICES_PATH,
  CONTRACTOR_INVOICES_PAYMENT_PATH,
} from '../../../../../constants';
import { useManagerUser } from '../../../../../hooks';
import { WrappedMixpanel } from '../../../../../mixpanelWrapper';

const SWIFT_FEES_ID = 'swift-fees';
const SWIFT_FEES_LABEL =
  'There might be SWIFT fees applicable. To ensure the contractor is paid properly, select one of these options:';
const SWIFT_FEES_SWIFT_OUR_LABEL = 'I will transfer using SWIFT OUR.';
const SWIFT_FEES_SWIFT_OUR_DESCRIPTION =
  'SWIFT OUR means that the fees to transfer are borne by the sender.';
const SWIFT_FEES_SEND_TO_CONTRACTOR_LABEL =
  'Send the amount received to the contractor(s).';
const SWIFT_FEES_SEND_TO_CONTRACTOR_DESCRIPTION =
  'In case bank fees are applied, contractors might receive less amount than invoiced. If the payment consists of multiple invoices, the amount received will be split proportionally. Should the gap between amount received and required is more than $50, the payment will be cancelled and you will be refunded.';

enum SwiftFeesEnum {
  SWIFT_OUR = 'swift-our',
  SEND_TO_CONTRACTOR = 'send-to-contractor',
}

const SWIFT_FEES = {
  id: SWIFT_FEES_ID,
  label: SWIFT_FEES_LABEL,
  options: [
    {
      id: SwiftFeesEnum.SWIFT_OUR,
      label: SWIFT_FEES_SWIFT_OUR_LABEL,
      description: SWIFT_FEES_SWIFT_OUR_DESCRIPTION,
    },
    {
      id: SwiftFeesEnum.SEND_TO_CONTRACTOR,
      label: SWIFT_FEES_SEND_TO_CONTRACTOR_LABEL,
      description: SWIFT_FEES_SEND_TO_CONTRACTOR_DESCRIPTION,
    },
  ],
};

const InvoiceQuoteBreakdownSkeleton = () => (
  <Card maxW={600} w="stretch" p="16">
    <Inline justify="space-between">
      <Inline>
        <Skeleton height={14} width={14} />
        <Skeleton height={14} width={60} />
      </Inline>
      <Inline>
        <Skeleton height={14} width={60} />
        <Skeleton height={14} width={14} />
      </Inline>
    </Inline>
  </Card>
);

type Props = {
  selectedPaymentMethod?: PaymentMethod;
  onClick: (method: PaymentMethod) => void;
  onStartPayment: (data: ContractorPayment) => void;
  invoices: ContractorInvoice[];
};

export const PaymentMethodStep = ({
  selectedPaymentMethod,
  onClick,
  onStartPayment,
  invoices,
}: Props) => {
  const navigate = useNavigate();

  const [allowPartialPayment, setAllowPartialPayment] = useState<boolean>();

  const { userId } = useManagerUser();

  const {
    mutate: createQuote,
    data: quote,
    isPending: isQuoteLoading,
  } = useCreateContractorInvoiceQuoteMutation();

  const {
    mutate: startPayment,
    data: paymentDetails,
    isPending: isPaymentCreationLoading,
    isSuccess: isPaymentCreationSuccess,
    isIdle: isPaymentCreationIdle,
  } = useCreateContractorInvoicePaymentMutation();

  useEffect(() => {
    if (isPaymentCreationIdle && invoices.length > 0) {
      const invoiceIds = invoices.map((invoice) => invoice.id);
      createQuote({ invoices: invoiceIds, allowPartialPayment });
    }
  }, [createQuote, invoices, allowPartialPayment, isPaymentCreationIdle]);

  useEffect(() => {
    if (isPaymentCreationSuccess) {
      onStartPayment(paymentDetails);
    }
  }, [paymentDetails, isPaymentCreationSuccess, onStartPayment]);

  return (
    <>
      {quote?.invoiceBreakdown && (
        <AccordionRoot type="multiple" css={{ width: 'stretch', maxW: 600 }}>
          {quote.invoiceBreakdown.map((invoiceQuote) => (
            <AccordionItem
              value={`${invoiceQuote.invoiceId}-quote`}
              key={`${invoiceQuote.invoiceId}-key`}
            >
              <AccordionHeader>
                <Inline align="center" gap="0">
                  <AccordionTrigger>
                    <Inline
                      justify="space-between"
                      w="stretch"
                      align="center"
                      pt="4"
                      pb="4"
                    >
                      <Typography as="span" size="14" weight="medium">
                        {invoiceQuote.invoiceRef}
                      </Typography>

                      <Typography as="span" size="14" weight="medium">
                        {formatNumber({
                          input: Number(invoiceQuote.transferAmount.amount),
                          currency: invoiceQuote.transferAmount.currency,
                        })}
                      </Typography>
                    </Inline>
                  </AccordionTrigger>
                  <Box p="4">
                    <Button
                      icon="X"
                      variant="tertiary"
                      disabled={invoices.length === 1}
                      onClick={(
                        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                      ) => {
                        event.stopPropagation();
                        const invoicesIds = invoices
                          .filter(
                            (invoice) => invoice.id !== invoiceQuote.invoiceId,
                          )
                          .map((invoice) => invoice.id);
                        navigate(`${CONTRACTOR_INVOICES_PAYMENT_PATH}`, {
                          state: {
                            invoicesIds,
                          },
                        });
                      }}
                    />
                  </Box>
                </Inline>
              </AccordionHeader>
              <AccordionContent>
                <Box pl="32" pr="32">
                  <ContractorInvoiceQuote invoiceQuote={invoiceQuote} />
                </Box>
              </AccordionContent>
            </AccordionItem>
          ))}
        </AccordionRoot>
      )}

      {isQuoteLoading && <InvoiceQuoteBreakdownSkeleton />}

      <Card maxW={600} w="stretch">
        <PaymentMethodSelector
          onPaymentMethodSelect={onClick}
          selectedPaymentMethod={selectedPaymentMethod}
        />
      </Card>
      <Card maxW={600} w="stretch">
        <Stack gap="24" p="32">
          <Typography size="16" as="h4" weight="medium" hideParagraphSpacing>
            Payment summary
          </Typography>
          <ContractorInvoiceQuote
            quoteSummary={quote?.quoteSummary}
            loading={isQuoteLoading}
          />
          <Stack>
            <RadioButtonGroup
              id={`${SWIFT_FEES.id}-group`}
              onChange={(event) => {
                const id = event.target.id;
                setAllowPartialPayment(id === SwiftFeesEnum.SEND_TO_CONTRACTOR);
              }}
              label={SWIFT_FEES.label}
              options={SWIFT_FEES.options.map((option, index) => ({
                id: option.id,
                label: option.label,
                name: SWIFT_FEES.id,
                defaultChecked: index === 0,
                helperText: option.description,
              }))}
            />
          </Stack>
          <Inline justify="space-between">
            <Button
              variant="secondary"
              onClick={() => {
                navigate(`${CONTRACTOR_INVOICES_PATH}`);
              }}
              disabled={isPaymentCreationLoading}
            >
              Cancel
            </Button>
            <Button
              disabled={
                !selectedPaymentMethod || isPaymentCreationLoading || !quote
              }
              loading={isPaymentCreationLoading}
              onClick={() => {
                WrappedMixpanel.track('Pay Continue Clicked', {
                  userId,
                });
                quote && startPayment({ quoteId: quote.quoteId });
              }}
            >
              Continue
            </Button>
          </Inline>
        </Stack>
      </Card>
    </>
  );
};
